<template>
    <div v-if="$store.state.user">
        <component-wrapper :config="component" @click.native="showDialog = true" />

        <v-dialog v-model="showDialog" max-width="600" scrollable>
            <v-card>
                <v-btn icon @click="showDialog = false" class="close-button">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
                <v-card-title class="headline pa-4">
                    <div>
                        <div class="text-h5 font-weight-bold ml-2">{{ $translate('Delete account') }}</div>
                        <div class="text-body-2 mt-2 ml-2 mr-2" style="white-space: normal;">
                            {{ $translate('Use the form below to request deletion of your account. Your request will be handled by the facility staff.') }}
                        </div>
                    </div>
                </v-card-title>
                <v-card-text class="pt-0">
                    <v-alert v-if="removalReasons.length > 0" outlined type="warning" class="mb-4">
                        <div>{{ $translate('Your account cannot be deleted due to the following reasons:') }}</div>
                        <ul class="mt-2 mb-0">
                            <li v-for="reason in removalReasons" :key="reason">{{ reason }}</li>
                        </ul>
                    </v-alert>
                    <custom-form
                        ref="customForm"
                        :id="$attrs.id"
                        :formTitle="formTitle"
                        :receivers="receivers"
                        :submitButtonText="submitButtonText"
                        :formAnswerTitle="formAnswerTitle"
                        :metaData="true"
                        :translate="true"
                        :isMail="isMail"
                        :externalMail="externalMail"
                        :isTodo="isTodo"
                        :daysUntilDueDate="daysUntilDueDate"
                        :rows="formRows"
                        :additionalDisabled="!userConfirmedDeletion || !canBeRemoved"
                        submitButtonColor="error"
                        :submitButtonOutlined="true"
                        @fieldChange="onFieldChange"
                    />
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
    <div v-else></div>
</template>

<script>
import CustomForm from '../components/CustomForm.vue';
import components from '../component.js';
import { getRandomId } from '../util.js';

export default {
    name: 'zoezi-request-account-removal',

    zoezi: {
        title: 'Delete account',
        icon: 'mdi-account-remove'
    },

    components: {
        CustomForm
    },

    props: {
        component: {
            title: 'Component',
            type: Object,
            fulltype: 'component',
            default: () => components.makeEmptyConfig({
                component: 'zoezi-button',
                props: {
                    text: window.$translate('Delete account'),
                    icon: 'mdi-account-remove',
                    color: 'error',
                    outlined: true,
                    block: true,
                }
            })
        },
        receivers: {
            title: 'Recipients',
            type: Array,
            fulltype: 'select',
            typeinfo: {
                multiple: true,
                text: 'name',
                value: 'id',
                placeholder: 'Select staff',
                nosort: true,
                getItems: () =>
                    window.$zoeziapi.get('/api/member/get/full').then((members) =>
                        members
                            .filter((member) => member.staff)
                            .map((x) => ({
                                id: x.id,
                                name: x.firstname ? x.firstname + ' ' + x.lastname : x.lastname,
                                mail: x.email,
                            }))
                    ),
            },
            default: () => [],
        },
        isMail: {
            title: 'Send as mail',
            type: Boolean,
            section: 'Mail',
            default: false,
        },
        externalMail: {
            title: 'External e-mail',
            type: String,
            section: 'Mail',
            default: '',
            typeinfo: {
                placeholder: 'example@gmail.com, example2@gmail.com',
            }
        },
        isTodo: {
            title: 'Create todo',
            type: Boolean,
            section: 'ToDo',
            default: true,
        },
        daysUntilDueDate: {
            title: 'Days until due date',
            type: Number,
            section: 'ToDo',
            default: 7,
        },
    },

    data() {
        const confirmationFieldId = getRandomId(8);
        return {
            showDialog: false,
            formTitle: 'Account deletion request',
            submitButtonText: 'Send request to delete your account',
            formAnswerTitle: 'Request sent',
            userConfirmedDeletion: false,
            canBeRemoved: true,
            removalCheck: null,
            confirmationFieldId,
            formRows: [
                {
                    id: confirmationFieldId,
                    component: 'zoezi-custom-form-field',
                    props: {
                        type: 'yesno',
                        text: 'Are you sure you want to request deletion of your account?',
                        required: true,
                        translate: true,
                        showIcon: false
                    }
                },
                {
                    id: getRandomId(8),
                    component: 'zoezi-custom-form-field',
                    props: {
                        type: 'textarea',
                        text: 'Message to the facility',
                        required: false,
                        translate: true
                    }
                }
            ]
        };
    },

    watch: {
        showDialog(val) {
            if (val) {
                this.checkCanBeRemoved();
            }
        }
    },

    computed: {
        removalReasons() {
            if (!this.removalCheck || this.removalCheck.result) {
                return [];
            }
            const reasons = [];
            if (this.removalCheck.inkassopayments) {
                reasons.push(this.$translate('You have unpaid debt collection payments'));
            }
            if (this.removalCheck.sentpayments) {
                reasons.push(this.$translate('You have sent payments that have not been processed'));
            }
            if (this.removalCheck.pendingpayments) {
                reasons.push(this.$translate('You have pending payments'));
            }
            if (this.removalCheck.trainingcards) {
                reasons.push(this.$translate('You have active training cards or memberships'));
            }
            if (this.removalCheck.valuecards) {
                reasons.push(this.$translate('You have remaining balance on value cards'));
            }
            if (this.removalCheck.permissions) {
                reasons.push(this.$translate('You are registered as staff'));
            }
            return reasons;
        }
    },

    methods: {
        onFieldChange(event) {
            // Track the yesno confirmation field value
            if (event.emitter && event.emitter.id === this.confirmationFieldId) {
                this.userConfirmedDeletion = event.content === true;
            }
        },
        checkCanBeRemoved() {
            this.$api.get('/api/memberapi/canBeRemoved').then(response => {
                this.removalCheck = response;
                this.canBeRemoved = response.result;
            }).catch(() => {
                this.removalCheck = null;
                this.canBeRemoved = false;
                this.showDialog = false;

                this.$store.commit('showErrorDialog', {
                    title: this.$translate('Could not verify if your account can be deleted. Please try again later or contact the facility staff'),
                });
            });
        }
    }
};
</script>

<style scoped>
.close-button {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 1;
}
</style>
